import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement } from 'react';
import { toast } from 'react-toastify';
import { rfqVendorAttachmentUrl } from '../../constants';
import type {
  RegionMasterDto,
  RequestForQuoteDetail,
  RfqVendorInvitationRow
} from '../../domain/types';
import {
  isRfqVendorRemovalLocked,
  isTubularStringQuoteRfq,
  normalizeRfqStatus
} from '../../domain/requestForQuote';
import type { VendorDirectoryEntry } from '../../domain/vendorDirectory';
import { RfqMaterialsSnapshotSection } from './RfqMaterialsSnapshotSection';
import { RfqPdfSidePanel } from './RfqPdfSidePanel';
import { RfqVendorQuotesComparison } from './RfqVendorQuotesComparison';
import { getFirstRankedQuotedVendorId } from './rfqVendorQuotesOrder';

/** Directory entries plus any vendors already on the RFQ so checkboxes never disappear if the directory loaded late. */
function mergeVendorPickList(
  directory: VendorDirectoryEntry[],
  invited: RfqVendorInvitationRow[] | undefined
): VendorDirectoryEntry[] {
  const byId = new Map<string, VendorDirectoryEntry>();
  for (const v of directory) {
    if (v.id) {
      byId.set(v.id, v);
    }
  }
  for (const inv of invited ?? []) {
    const id = inv.vendorId?.trim();
    if (!id || byId.has(id)) {
      continue;
    }
    const email = inv.vendorEmail?.trim();
    byId.set(id, {
      id,
      name: inv.vendorName?.trim() || '(Vendor)',
      address: '',
      ...(email ? { email } : {})
    });
  }
  return Array.from(byId.values()).sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
  );
}

export interface RfqWorkflowPanelProps {
  rfqDetail: RequestForQuoteDetail | null;
  rfqLoading: boolean;
  vendors: VendorDirectoryEntry[];
  /** Region catalog for displaying region names on the RFQ. */
  activeRegions: RegionMasterDto[];
  onCreateRfq: () => void | Promise<void>;
  onUpdateRfq: (
    id: string,
    payload: { requirements: string; incotermsSnapshot: string; vendorIds: string[] }
  ) => void | Promise<void>;
  onSendRfqInvitations: (id: string) => void | Promise<void>;
  onSelectVendorForRfq: (id: string, vendorId: string) => void | Promise<void>;
  onCancelRfq: (id: string) => void | Promise<void>;
  /** Navigates to the RFQ registry full page for this id. */
  onOpenFullPage?: (rfqId: string) => void;
  /** Opens the linked procurement request (optional; e.g. from RFQ tab). */
  onOpenProcurementRequest?: (procurementRequestId: string) => void;
  /** When true, materials and summary are shown but RFQ cannot be edited. */
  readOnly?: boolean;
  /** When false, omits the outer panel heading wrapper (e.g. full page supplies its own title). */
  showSectionHeading?: boolean;
  /** When false, empty state explains creating from a sourcing request instead of offering a create button. */
  allowCreate?: boolean;
  /** Refresh full vendor directory from the API (e.g. if the list was empty). */
  onReloadVendors?: () => void | Promise<void>;
  /** Required to load vendor quote PDF attachments (shown below the comparison tables). */
  authFetch?: (url: string, init?: RequestInit) => Promise<Response>;
  /** POST /api/operations/rfq/{id}/approve|reject — server enforces role. */
  canApproveRfqWorkflow?: boolean;
  onApproveRfq?: (id: string, comment?: string) => void | Promise<void>;
  onRejectRfq?: (id: string, comment?: string) => void | Promise<void>;
}

export function rfqStatusLabel(status: string): string {
  const key = normalizeRfqStatus(status);
  const map: Record<string, string> = {
    draft: 'Draft',
    rejected: 'Rejected',
    requestsent: 'Request sent',
    partialreceived: 'Partial received',
    received: 'Received',
    pendingapproval: 'Pending approval',
    approved: 'Approved',
    cancelled: 'Cancelled',
    complete: 'Complete'
  };
  return map[key] ?? status;
}

function vendorHasQuoteSubmitted(status: string): boolean {
  return status.toLowerCase().replace(/\s/g, '') === 'quotereceived';
}

function rfqVendorResponseLabel(status: string): string {
  const k = status.replace(/\s/g, '');
  const map: Record<string, string> = {
    pending: 'Pending',
    Pending: 'Pending',
    sent: 'Sent',
    Sent: 'Sent',
    quoteReceived: 'Quote received',
    QuoteReceived: 'Quote received'
  };
  return map[k] ?? map[status] ?? status;
}

export function RfqWorkflowPanel({
  rfqDetail,
  rfqLoading,
  vendors,
  activeRegions,
  onCreateRfq,
  onUpdateRfq,
  onSendRfqInvitations,
  onSelectVendorForRfq,
  onCancelRfq,
  onOpenFullPage,
  onOpenProcurementRequest,
  readOnly = false,
  showSectionHeading = true,
  allowCreate = true,
  onReloadVendors,
  authFetch,
  canApproveRfqWorkflow = false,
  onApproveRfq,
  onRejectRfq
}: RfqWorkflowPanelProps): ReactElement {
  const [rfqRequirements, setRfqRequirements] = useState('');
  const [rfqIncoterms, setRfqIncoterms] = useState('');
  const [rfqVendorIds, setRfqVendorIds] = useState<string[]>([]);
  const [rfqSaveBusy, setRfqSaveBusy] = useState(false);
  const [rfqSendBusy, setRfqSendBusy] = useState(false);
  const [rfqSelectBusy, setRfqSelectBusy] = useState(false);
  const [rfqCancelBusy, setRfqCancelBusy] = useState(false);
  const [rfqOpsComment, setRfqOpsComment] = useState('');
  const [rfqOpsBusy, setRfqOpsBusy] = useState(false);
  const [quotePdfUrl, setQuotePdfUrl] = useState<string | null>(null);
  const [quotePdfTitle, setQuotePdfTitle] = useState('');
  const quotePdfRef = useRef<string | null>(null);

  const closeQuotePdf = useCallback(() => {
    if (quotePdfRef.current) {
      URL.revokeObjectURL(quotePdfRef.current);
      quotePdfRef.current = null;
    }
    setQuotePdfUrl(null);
    setQuotePdfTitle('');
  }, []);

  useEffect(
    () => () => {
      if (quotePdfRef.current) {
        URL.revokeObjectURL(quotePdfRef.current);
        quotePdfRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    closeQuotePdf();
  }, [rfqDetail?.id, closeQuotePdf]);

  const openVendorQuoteAttachment = useCallback(
    async (vendorId: string, fileName: string) => {
      if (!rfqDetail || !authFetch) {
        toast.error('Attachment preview is not available.');
        return;
      }
      closeQuotePdf();
      try {
        const res = await authFetch(rfqVendorAttachmentUrl(rfqDetail.id, vendorId));
        if (!res.ok) {
          const text = await res.text();
          toast.error(text || `Could not open attachment (${res.status}).`);
          return;
        }
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        quotePdfRef.current = url;
        setQuotePdfUrl(url);
        setQuotePdfTitle(fileName || 'Quote attachment');
      } catch {
        toast.error('Could not load the attachment.');
      }
    },
    [authFetch, closeQuotePdf, rfqDetail]
  );

  useEffect(() => {
    if (!rfqDetail) {
      setRfqRequirements('');
      setRfqIncoterms('');
      setRfqVendorIds([]);
      return;
    }
    setRfqRequirements(rfqDetail.requirements ?? '');
    setRfqIncoterms(rfqDetail.incotermsSnapshot ?? '');
    setRfqVendorIds(rfqDetail.vendors.map((v) => v.vendorId));
  }, [rfqDetail]);

  useEffect(() => {
    setRfqOpsComment('');
  }, [rfqDetail?.id]);

  const sortedRfqActivityLog = useMemo(() => {
    const list = rfqDetail?.activityLog ?? [];
    return [...list].sort(
      (a, b) => new Date(a.atUtc).getTime() - new Date(b.atUtc).getTime()
    );
  }, [rfqDetail?.activityLog]);

  function addRfqVendorFromDropdown(vendorId: string): void {
    if (!vendorId.trim()) {
      return;
    }
    setRfqVendorIds((prev) => (prev.includes(vendorId) ? prev : [...prev, vendorId]));
  }

  function removeRfqVendor(vendorId: string): void {
    const inv = rfqDetail?.vendors.find((x) => x.vendorId === vendorId);
    if (isRfqVendorRemovalLocked(inv)) {
      toast.error(
        'This vendor was already sent a quote request or submitted a quote and cannot be removed from this RFQ.'
      );
      return;
    }
    setRfqVendorIds((prev) => prev.filter((x) => x !== vendorId));
  }

  const vendorPickList = useMemo(
    () => mergeVendorPickList(vendors, rfqDetail?.vendors),
    [vendors, rfqDetail?.vendors]
  );

  const vendorsAvailableToAdd = useMemo(() => {
    const chosen = new Set(rfqVendorIds);
    return vendorPickList.filter((v) => !chosen.has(v.id));
  }, [vendorPickList, rfqVendorIds]);

  const rfqStatus = useMemo(() => normalizeRfqStatus(rfqDetail?.status), [rfqDetail?.status]);

  const showTubularComparison = useMemo(() => {
    if (!rfqDetail) {
      return false;
    }
    return (
      isTubularStringQuoteRfq(rfqDetail.materialsSnapshot ?? []) &&
      rfqDetail.vendors.some((v) => vendorHasQuoteSubmitted(v.responseStatus)) &&
      ['requestsent', 'partialreceived', 'received', 'pendingapproval', 'approved', 'rejected'].includes(rfqStatus)
    );
  }, [rfqDetail, rfqStatus]);

  const canEditRfqDraft =
    !!rfqDetail && (rfqStatus === 'draft' || rfqStatus === 'rejected') && !readOnly;

  const readOnlyRfqMeta = !!rfqDetail && !canEditRfqDraft;

  const inner = (
    <>
      {showSectionHeading ? <h4>Request for quote</h4> : null}
      {rfqLoading ? (
        <p className="hint">Loading quote request…</p>
      ) : !rfqDetail ? (
        <>
          {allowCreate ? (
            <>
              <p className="hint">
                Create an RFQ to capture material lines from the procurement request, add requirements, choose which
                vendors should receive a quote request (each needs an email on their directory record), then send
                invitations.
              </p>
              <button type="button" onClick={() => void onCreateRfq()}>
                Create request for quote
              </button>
            </>
          ) : (
            <p className="hint">
              Open a procurement request that is in <strong>Sourcing</strong> and create a request for quote from the
              request panel.
            </p>
          )}
        </>
      ) : (
        <>
          <p className="hint">
            <strong>{rfqDetail.rfqNumber}</strong> · {rfqStatusLabel(rfqStatus)}
            {onOpenFullPage ? (
              <>
                {' '}
                <button type="button" className="secondary" onClick={() => onOpenFullPage(rfqDetail.id)}>
                  Open full page
                </button>
              </>
            ) : null}
          </p>

          <RfqMaterialsSnapshotSection
            materials={rfqDetail.materialsSnapshot ?? []}
            requestNumber={rfqDetail.requestNumber}
            regionCode={rfqDetail.region}
            procurementRequestId={rfqDetail.procurementRequestId}
            rfqDocumentId={rfqDetail.id}
            activeRegions={activeRegions}
            onOpenProcurementRequest={onOpenProcurementRequest}
          />

          {readOnly && rfqDetail && (rfqStatus === 'draft' || rfqStatus === 'rejected') && (
            <p className="hint full-width">
              Your account is <strong>view-only</strong> for this app — vendor selection and RFQ edits are disabled.
            </p>
          )}

          {readOnlyRfqMeta && (
            <div className="full-width rfq-readonly-meta">
              <h5 className="po-subheading full-width">Requirements &amp; special terms</h5>
              <p className="multiline">{rfqDetail.requirements.trim() ? rfqDetail.requirements : '—'}</p>
              <h5 className="po-subheading full-width">Incoterms</h5>
              <p>{rfqDetail.incotermsSnapshot.trim() ? rfqDetail.incotermsSnapshot : '—'}</p>
            </div>
          )}

          {readOnlyRfqMeta &&
            ['requestsent', 'partialreceived', 'received'].includes(rfqStatus) && (
              <p className="hint full-width">
                Vendor selection is locked after invitations are sent. This RFQ must be in <strong>Draft</strong> or{' '}
                <strong>Rejected</strong> to change which vendors receive the quote request.
              </p>
            )}

          {canEditRfqDraft && (
            <div className="nested-card rfq-edit-section full-width">
              <header className="rfq-instructions-header">
                <h5 className="po-subheading rfq-section-title">Instructions to vendors</h5>
                <p className="hint rfq-instructions-lead">
                  These notes go to vendors with the quote request. Keep pricing expectations, delivery, and testing
                  requirements explicit.
                </p>
              </header>
              <div className="rfq-instructions-fields">
                <label className="full-width rfq-field rfq-field-requirements">
                  <span className="rfq-field-label">Requirements &amp; special terms</span>
                  <textarea
                    rows={5}
                    value={rfqRequirements}
                    onChange={(e) => setRfqRequirements(e.target.value)}
                    placeholder="Written instructions for vendors (pricing expectations, delivery, testing, etc.)…"
                  />
                </label>
                <label className="full-width rfq-field rfq-field-incoterms">
                  <span className="rfq-field-label">Incoterms (aligned with the request)</span>
                  <input
                    value={rfqIncoterms}
                    onChange={(e) => setRfqIncoterms(e.target.value)}
                    placeholder="e.g. FOB, DDP"
                  />
                </label>
              </div>

              <h5 className="po-subheading full-width rfq-section-title rfq-vendors-section-title">
                Vendors to receive quote requests
              </h5>
              <p className="hint full-width">
                Choose vendors from the dropdown to add them to this RFQ. When you <strong>save and email secure
                links</strong>, only vendors who have <strong>not</strong> been emailed for this RFQ yet receive a new
                one-time link. Vendors who were already sent a quote request or who submitted a quote cannot be removed
                (email must be set in the Vendor directory for new invitees).
              </p>
              {vendorPickList.length === 0 ? (
                <div className="full-width">
                  <p className="error">No vendors found. Add vendors under the Vendors tab (with email for RFQ), then refresh.</p>
                  {onReloadVendors ? (
                    <button type="button" className="secondary" onClick={() => void onReloadVendors()}>
                      Refresh vendor list
                    </button>
                  ) : null}
                </div>
              ) : (
                <div className="full-width rfq-vendor-dropdown-block">
                  <label className="full-width">
                    Add vendor
                    <select
                      key={rfqVendorIds.join('|')}
                      value=""
                      onChange={(e) => {
                        const id = e.target.value;
                        if (id) {
                          addRfqVendorFromDropdown(id);
                        }
                      }}
                      disabled={vendorsAvailableToAdd.length === 0}
                    >
                      <option value="">
                        {vendorsAvailableToAdd.length === 0
                          ? 'All available vendors are already selected'
                          : 'Choose a vendor to add…'}
                      </option>
                      {vendorsAvailableToAdd.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                          {v.email?.trim() ? ` · ${v.email.trim()}` : ' (no email yet)'}
                        </option>
                      ))}
                    </select>
                  </label>
                  <div className="full-width rfq-selected-vendors">
                    <h6 className="po-subheading">Selected for this RFQ ({rfqVendorIds.length})</h6>
                    {rfqVendorIds.length === 0 ? (
                      <p className="hint">No vendors selected yet — use the dropdown above.</p>
                    ) : (
                      <ul className="compact-list rfq-selected-vendor-list">
                        {rfqVendorIds.map((id) => {
                          const v = vendorPickList.find((x) => x.id === id);
                          const inv = rfqDetail?.vendors.find((x) => x.vendorId === id);
                          const removeLocked = isRfqVendorRemovalLocked(inv);
                          if (!v) {
                            return (
                              <li key={id}>
                                <span className="hint">Unknown vendor ({id})</span>{' '}
                                <button
                                  type="button"
                                  className="secondary"
                                  disabled={removeLocked}
                                  title={
                                    removeLocked
                                      ? 'Already invited or quoted; cannot remove.'
                                      : undefined
                                  }
                                  onClick={() => removeRfqVendor(id)}
                                >
                                  Remove
                                </button>
                              </li>
                            );
                          }
                          return (
                            <li key={id} className="rfq-selected-vendor-row">
                              <span>
                                <strong>{v.name}</strong>
                                {v.email?.trim() ? (
                                  <span className="hint"> · {v.email.trim()}</span>
                                ) : (
                                  <span className="hint"> — add email under Vendors</span>
                                )}
                              </span>
                              <button
                                type="button"
                                className="secondary"
                                disabled={removeLocked}
                                title={
                                  removeLocked
                                    ? 'Already invited or quoted; cannot remove.'
                                    : undefined
                                }
                                onClick={() => removeRfqVendor(id)}
                              >
                                Remove
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </div>
                </div>
              )}
              {onReloadVendors && vendorPickList.length > 0 ? (
                <p className="hint full-width">
                  <button type="button" className="secondary" onClick={() => void onReloadVendors()}>
                    Refresh vendor list
                  </button>{' '}
                  ({vendorPickList.length} vendors)
                </p>
              ) : null}
              <div className="row">
                <button
                  type="button"
                  disabled={rfqSendBusy || rfqVendorIds.length === 0 || vendorPickList.length === 0}
                  onClick={() => {
                    void (async () => {
                      const missingEmail = rfqVendorIds.filter((id) => {
                        const v = vendorPickList.find((x) => x.id === id);
                        return v && !v.email?.trim();
                      });
                      if (missingEmail.length > 0) {
                        toast.error('Every selected vendor must have an email on file. Update Vendors, refresh, then try again.');
                        return;
                      }
                      setRfqSendBusy(true);
                      try {
                        await onUpdateRfq(rfqDetail.id, {
                          requirements: rfqRequirements,
                          incotermsSnapshot: rfqIncoterms,
                          vendorIds: rfqVendorIds
                        });
                        await onSendRfqInvitations(rfqDetail.id);
                      } finally {
                        setRfqSendBusy(false);
                      }
                    })();
                  }}
                >
                  {rfqSendBusy ? 'Saving & sending…' : 'Save & email secure quote links'}
                </button>
                <button
                  type="button"
                  className="secondary"
                  disabled={rfqSaveBusy}
                  onClick={() => {
                    void (async () => {
                      setRfqSaveBusy(true);
                      try {
                        await onUpdateRfq(rfqDetail.id, {
                          requirements: rfqRequirements,
                          incotermsSnapshot: rfqIncoterms,
                          vendorIds: rfqVendorIds
                        });
                      } finally {
                        setRfqSaveBusy(false);
                      }
                    })();
                  }}
                >
                  {rfqSaveBusy ? 'Saving…' : 'Save draft only (no email)'}
                </button>
                {rfqStatus === 'draft' && (
                  <button
                    type="button"
                    className="danger"
                    disabled={rfqCancelBusy}
                    onClick={() => {
                      void (async () => {
                        setRfqCancelBusy(true);
                        try {
                          await onCancelRfq(rfqDetail.id);
                        } finally {
                          setRfqCancelBusy(false);
                        }
                      })();
                    }}
                  >
                    {rfqCancelBusy ? 'Cancelling…' : 'Cancel RFQ'}
                  </button>
                )}
              </div>
            </div>
          )}

          {['requestsent', 'partialreceived', 'received', 'pendingapproval', 'approved', 'rejected'].includes(
            rfqStatus
          ) && (
            <>
              {showTubularComparison && authFetch ? (
                <div className="rfq-vendor-quotes-stack full-width">
                  <RfqVendorQuotesComparison
                    rfqId={rfqDetail.id}
                    rfqNumber={rfqDetail.rfqNumber}
                    materials={rfqDetail.materialsSnapshot ?? []}
                    vendors={rfqDetail.vendors}
                    onOpenAttachment={openVendorQuoteAttachment}
                    readOnly={readOnly}
                    lockRecommendationOrder={rfqStatus === 'approved'}
                    pinVendorIdFirst={rfqStatus === 'approved' ? rfqDetail.selectedVendorId ?? null : null}
                  />
                  {quotePdfUrl ? (
                    <RfqPdfSidePanel title={quotePdfTitle} blobUrl={quotePdfUrl} onClose={closeQuotePdf} />
                  ) : null}
                </div>
              ) : null}
              {(!showTubularComparison || !authFetch) && (
                <div className="full-width">
                  <h5 className="po-subheading">Vendor quote status</h5>
                  <ul className="compact-list">
                    {rfqDetail.vendors.map((v) => (
                      <li key={v.vendorId}>
                        <strong>{v.vendorName}</strong>
                        {v.vendorEmail?.trim() ? ` · ${v.vendorEmail.trim()}` : ''}:{' '}
                        {rfqVendorResponseLabel(v.responseStatus)}
                        {v.quoteReceivedAtUtc ? ` · Quote ${new Date(v.quoteReceivedAtUtc).toLocaleString()}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          )}
          {(rfqStatus === 'received' ||
            rfqStatus === 'partialreceived' ||
            rfqStatus === 'rejected') &&
            !readOnly && (
            <div className="full-width">
              <p className="hint">
                Submission uses the <strong>first-ranked</strong> vendor with a quote (leftmost in the recommendation
                strip, or earliest quote received when the strip is not shown). Adjust the strip order before
                submitting if needed.
                {rfqStatus === 'rejected' ? (
                  <>
                    {' '}
                    After operations rejection you can add vendors and save, email only new invitees, then submit again
                    — or submit again if you only change rank among vendors who already quoted.
                  </>
                ) : null}
              </p>
              <button
                type="button"
                disabled={rfqSelectBusy}
                onClick={() => {
                  void (async () => {
                    const invited = rfqDetail.vendors.filter((v) => v.vendorName?.trim());
                    const firstId = getFirstRankedQuotedVendorId(rfqDetail.id, invited);
                    if (!firstId) {
                      toast.error(
                        'No vendor with a submitted quote was found. Wait for at least one quote before submitting.'
                      );
                      return;
                    }
                    setRfqSelectBusy(true);
                    try {
                      await onSelectVendorForRfq(rfqDetail.id, firstId);
                    } finally {
                      setRfqSelectBusy(false);
                    }
                  })();
                }}
              >
                {rfqSelectBusy ? 'Submitting…' : 'Submit for operations approval'}
              </button>
            </div>
          )}
          {rfqStatus === 'pendingapproval' &&
            (canApproveRfqWorkflow && onApproveRfq && onRejectRfq ? (
              <div className="full-width rfq-ops-decision">
                <label htmlFor="rfq-ops-comment" className="sr-only">
                  Comment (optional)
                </label>
                <textarea
                  id="rfq-ops-comment"
                  className="full-width"
                  rows={2}
                  placeholder="Optional comment for approve or reject"
                  value={rfqOpsComment}
                  onChange={(e) => setRfqOpsComment(e.target.value)}
                  disabled={rfqOpsBusy}
                />
                <div className="panel-actions rfq-ops-actions">
                  <button
                    type="button"
                    className="primary"
                    disabled={rfqOpsBusy}
                    onClick={() => {
                      if (!rfqDetail) {
                        return;
                      }
                      void (async () => {
                        setRfqOpsBusy(true);
                        try {
                          await onApproveRfq(rfqDetail.id, rfqOpsComment.trim() || undefined);
                          setRfqOpsComment('');
                        } finally {
                          setRfqOpsBusy(false);
                        }
                      })();
                    }}
                  >
                    {rfqOpsBusy ? 'Working…' : 'Approve recommendation'}
                  </button>
                  <button
                    type="button"
                    className="secondary"
                    disabled={rfqOpsBusy}
                    onClick={() => {
                      if (!rfqDetail) {
                        return;
                      }
                      void (async () => {
                        setRfqOpsBusy(true);
                        try {
                          await onRejectRfq(rfqDetail.id, rfqOpsComment.trim() || undefined);
                          setRfqOpsComment('');
                        } finally {
                          setRfqOpsBusy(false);
                        }
                      })();
                    }}
                  >
                    Reject
                  </button>
                </div>
              </div>
            ) : (
              <p className="hint">Awaiting operations manager approval.</p>
            ))}
          {rfqStatus === 'approved' && (
            <div className="full-width rfq-post-approval-next-steps">
              <p className="hint">
                <strong>Operations approved this recommendation.</strong> The approved vendor is final — drag-to-rank
                in the quote comparison (when shown) is disabled. Compare vendor PDFs above if you need to review them.
              </p>
              <p className="hint">
                Next steps for procurement: create the purchase order from the linked procurement request (on the{' '}
                <strong>Requests</strong> tab or full request page while the request is in sourcing / order placed),
                then open the PO and use <strong>Export PDF</strong> — send that file to the vendor as an email
                attachment.
              </p>
            </div>
          )}
          {rfqDetail.operationsDecisionComment ? (
            <p className="hint">Operations: {rfqDetail.operationsDecisionComment}</p>
          ) : null}

          <details className="activity-log-details">
            <summary>
              RFQ activity log
              {sortedRfqActivityLog.length > 0 ? ` (${sortedRfqActivityLog.length})` : ''}
            </summary>
            {sortedRfqActivityLog.length === 0 ? (
              <p className="hint activity-log-empty">No workflow events recorded yet.</p>
            ) : (
              <ul className="activity-log-list">
                {sortedRfqActivityLog.map((entry, idx) => (
                  <li key={`${entry.atUtc}-${idx}`} className="activity-log-item">
                    <div className="activity-log-meta">
                      <strong>{entry.actorDisplayName?.trim() || entry.actorUsername || 'User'}</strong>
                      <span className="activity-log-when">
                        {entry.atUtc ? new Date(entry.atUtc).toLocaleString() : ''}
                      </span>
                    </div>
                    <div className="activity-log-message">{entry.message}</div>
                  </li>
                ))}
              </ul>
            )}
          </details>
        </>
      )}
    </>
  );

  return showSectionHeading ? <div className="panel-actions">{inner}</div> : inner;
}
